import {useCallback, useEffect, useState} from 'react'
import {Button, ButtonGroup} from 'react-bootstrap'
import Modal from '../../Modal'
import {AudienceRequestModel, AudienceModel} from '../../../redux/campaignTypes'
import IndividualsAudienceEditor from './IndividualsAudienceEditor'
import ActiveEmployeesAudienceEditor from './ActiveEmployeesAudienceEditor'
import ActiveUsersAudienceEditor from './ActiveUsersAudienceEditor'
import useCampaignActions from "../../../hooks/useCampaignActions"
import {createUseStyles} from "react-jss";

interface Props {
  campaignId: string
  initialValue: AudienceModel | undefined
  visible: boolean
  onCloseClick?: () => void
}

const createEmptyAudienceRequestModel = (type: AudienceRequestModel['type'] | undefined): AudienceRequestModel => {
  switch (type) {
    case 'individuals':
      return ({type: 'individuals', userIds: []})
    case 'activeEmployees':
      return ({type: 'activeEmployees'})
    case 'activeUsers':
      return ({type: 'activeUsers'})
    default:
      return ({type: 'individuals', userIds: []})
  }
}

const AudienceEditorModal = ({campaignId, initialValue, visible, onCloseClick}: Props) => {
  const styles = useStyles()
  const [requestModel, setRequestModel] = useState<AudienceRequestModel>({type: 'individuals', userIds: []})

  const {setCampaignAudience} = useCampaignActions()

  useEffect(() => {
    setRequestModel(createEmptyAudienceRequestModel(initialValue?.type))
  }, [initialValue]);

  const handleSave = () => {
    if (!!requestModel) {
      setCampaignAudience(campaignId, requestModel)
    }

    onCloseClick && onCloseClick()
  }

  const handleCancel = () => {
    onCloseClick && onCloseClick()
  }

  const handleAudienceChange = useCallback((next: AudienceRequestModel) => {
    setRequestModel(next)
  }, [setRequestModel])

  const renderTypeSelector = () => {
    const types: { key: AudienceRequestModel['type'], label: string }[] = [
      {key: 'individuals', label: 'Individuals'},
      {key: 'activeEmployees', label: 'Active Employees'},
      {key: 'activeUsers', label: 'Active Users'}
    ]

    return (
      <div className={styles.typeSelector}>
        <ButtonGroup className={styles.buttonGroup}>
          {types.map(({key, label}) => (
            <Button
              key={key}
              variant={key === requestModel.type ? 'primary' : 'outline-primary'}
              onClick={() => {
                if (requestModel.type !== key) {
                  setRequestModel(createEmptyAudienceRequestModel(key))
                }
              }}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </div>
    )
  }

  return (
    <Modal
      size={"lg"}
      title={'Edit audience'}
      show={visible}
      okButtonTitle='Save'
      cancelButtonTitle='Cancel'
      onHide={handleCancel}
      onOkClick={handleSave}
      onCancelClick={handleCancel}
    >
      <div style={{minHeight: 500}}>
        {renderTypeSelector()}

        {requestModel.type === 'individuals' && (
          <IndividualsAudienceEditor
            audience={initialValue?.type === 'individuals' ? initialValue : undefined}
            onChange={handleAudienceChange}
          />
        )}

        {requestModel.type === 'activeEmployees' && (
          <ActiveEmployeesAudienceEditor
            audience={initialValue?.type === 'activeEmployees' ? initialValue : undefined}
            onChange={handleAudienceChange}
          />
        )}

        {requestModel.type === 'activeUsers' && (
          <ActiveUsersAudienceEditor
            audience={initialValue?.type === 'activeUsers' ? initialValue : undefined}
            onChange={handleAudienceChange}
          />
        )}
      </div>
    </Modal>
  )
}

const useStyles = createUseStyles({
  typeSelector: {
    marginBottom: 20
  },
  buttonGroup: {
    '& .btn-outline-primary': {
      color: '#ffffff !important',
      backgroundColor: '#6c757d !important',
      borderColor: '#6c757d !important',

      '&:hover': {
        color: '#ffffff !important',
        backgroundColor: '#5a6268 !important',
        borderColor: '#545b62 !important'
      },

      '&:focus': {
        color: '#ffffff !important',
        backgroundColor: '#5a6268 !important',
        borderColor: '#545b62 !important',
      },

      '&:active': {
        color: '#ffffff !important',
        backgroundColor: '#545b62 !important',
        borderColor: '#4e555b !important'
      }
    },
    '& .btn-primary': {
      borderColor: '#6c757d !important',

      '&:hover': {
        color: '#ffffff !important',
        backgroundColor: '#5a6268 !important',
        borderColor: '#545b62 !important'
      }
    }
  }
})


export default AudienceEditorModal
